import type { CSSProperties } from 'react';

/** Per-role fallback colors/behavior for a product action button (add to cart / buy now /
 *  negotiate) — each role ships its own distinct defaults so the three read as visually
 *  differentiated purchase intents out of the box. */
export interface ActionButtonDefaults {
    bgColor: string;
    textColor: string;
    hoverBgColor: string;
    hoverTextColor: string;
    borderColor?: string; // default 'transparent'
    borderRadius?: number; // default 8
    /** "Kup teraz"/"Negocjuj" always force height:auto once padding fields exist as settings
     *  keys; "Dodaj do koszyka" only does so when the merchant actually sets custom padding —
     *  preserved as-is (a pre-existing inconsistency in the original component) rather than
     *  silently unified, so refactoring into this shared helper changes zero rendered output. */
    forceAutoHeight?: boolean;
}

/** Reads `settings[\`${prefix}X\`]` (e.g. `btnBgColor`, `buyNowBgColor`, `negotiateBgColor`) and
 *  builds the button's base (non-hover) inline style. Shared by `ProductAddToCartBlock` (product
 *  page) and `BuilderProductCard` (Produkty grid) so both compute a button's look identically. */
export function buildActionButtonStyle(settings: any, prefix: string, defaults: ActionButtonDefaults): CSSProperties {
    const borderWidth: number = settings[`${prefix}BorderWidth`] ?? 0;
    const bgColor: string = settings[`${prefix}BgColor`] || defaults.bgColor;
    const textColor: string = settings[`${prefix}TextColor`] || defaults.textColor;
    const borderColor: string = settings[`${prefix}BorderColor`] || defaults.borderColor || 'transparent';
    const borderRadius: number = settings[`${prefix}BorderRadius`] ?? defaults.borderRadius ?? 8;
    const paddingY: number | undefined = settings[`${prefix}PaddingY`];
    const paddingX: number | undefined = settings[`${prefix}PaddingX`];
    const fontSize: number | undefined = settings[`${prefix}FontSize`];
    const fontWeight: string | undefined = settings[`${prefix}FontWeight`];
    const textTransform: string | undefined = settings[`${prefix}TextTransform`];
    const width: number | undefined = settings[`${prefix}Width`];
    const hasCustomPadding = paddingY !== undefined || paddingX !== undefined;

    return {
        backgroundColor: bgColor,
        color: textColor,
        borderColor,
        borderWidth: borderWidth > 0 ? `${borderWidth}px` : undefined,
        borderStyle: borderWidth > 0 ? 'solid' : undefined,
        borderRadius: `${borderRadius}px`,
        // forceAutoHeight lets a long label wrap; without its own padding it collapsed the button to
        // the text's height (a thin bar under "Dodaj do koszyka") — keep a normal button's size then.
        padding: hasCustomPadding ? `${paddingY ?? 10}px ${paddingX ?? 24}px` : (defaults.forceAutoHeight ? '10px 24px' : undefined),
        minHeight: defaults.forceAutoHeight && !hasCustomPadding ? '2.5rem' : undefined,
        fontSize: fontSize ? `${fontSize}px` : undefined,
        fontWeight: fontWeight || undefined,
        textTransform: (textTransform as any) || undefined,
        width: width ? `${width}%` : undefined,
        height: (defaults.forceAutoHeight || hasCustomPadding) ? 'auto' : undefined,
    };
}

/** Builds the `.{className}:hover:not(:disabled) { ... }` CSS block for a single button —
 *  `className` must be caller-supplied and unique per rendered instance (not just per block),
 *  since e.g. the Produkty grid renders one card per product and each needs its own scoped hover
 *  rule, or hovering one card's button would visually affect every other card's on the page. */
export function buildActionButtonHoverCSS(settings: any, prefix: string, className: string, defaults: ActionButtonDefaults): string {
    const borderColor: string = settings[`${prefix}BorderColor`] || defaults.borderColor || 'transparent';
    const hoverBgColor: string = settings[`${prefix}HoverBgColor`] || defaults.hoverBgColor;
    const hoverTextColor: string = settings[`${prefix}HoverTextColor`] || defaults.hoverTextColor;
    const hoverBorderColor: string = settings[`${prefix}HoverBorderColor`] || borderColor;

    return `
        .${className}:hover:not(:disabled) {
            background-color: ${hoverBgColor} !important;
            color: ${hoverTextColor} !important;
            border-color: ${hoverBorderColor} !important;
        }
    `;
}

export const ACTION_BUTTON_DEFAULTS: Record<'btn' | 'buyNow' | 'negotiate', ActionButtonDefaults> = {
    btn: { bgColor: '#2563eb', textColor: '#ffffff', hoverBgColor: '#1d4ed8', hoverTextColor: '#ffffff' },
    buyNow: { bgColor: '#16a34a', textColor: '#ffffff', hoverBgColor: '#15803d', hoverTextColor: '#ffffff', forceAutoHeight: true },
    negotiate: { bgColor: '#f59e0b', textColor: '#ffffff', hoverBgColor: '#d97706', hoverTextColor: '#ffffff', forceAutoHeight: true },
};
